import { useState } from 'react';
import { FileText } from 'lucide-react';
import { useT } from '@/store/i18n';
import { useAuth } from '@/store/auth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { getToken } from '@/lib/api';
import { SaveExamSetModal } from '@/components/SaveExamSetModal';
import type { Question } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
  cart: number[];
  questions: Question[];
}

export function ExamBuilderModal({ open, onClose, cart, questions }: Props) {
  const t = useT();
  const user = useAuth((s) => s.user);

  const cartQuestions = cart.map((id) => questions.find((q) => q.id === id)).filter(Boolean) as Question[];
  const firstQ = cartQuestions[0];
  const defaultPoints = cartQuestions.reduce((sum, q) => sum + (q.points ?? 1), 0);

  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [schoolName, setSchoolName] = useState('โรงเรียนใบบุญลำพูน');
  const [subjectName, setSubjectName] = useState(firstQ?.subject_name ?? '');
  const [gradeLevel, setGradeLevel] = useState(firstQ?.grade_name ?? '');
  const [teacherName, setTeacherName] = useState(user?.full_name ?? '');
  const [examDate, setExamDate] = useState('');
  const [timeLimit, setTimeLimit] = useState('60 นาที');
  const [totalPoints, setTotalPoints] = useState(String(defaultPoints || cart.length));
  const [variantLabel, setVariantLabel] = useState('');
  const [includeAnswerKey, setIncludeAnswerKey] = useState(true);
  const [shuffleOptions, setShuffleOptions] = useState(false);
  const [columns, setColumns] = useState<1 | 2>(1);
  const [multiMode, setMultiMode] = useState(false);
  const [versionCount, setVersionCount] = useState(2);
  const [titleError, setTitleError] = useState('');
  const [saveSetOpen, setSaveSetOpen] = useState(false);

  function buildMetadata() {
    return { instructions, school_name: schoolName, subject_name: subjectName, grade_level: gradeLevel, teacher_name: teacherName, exam_date: examDate, time_limit: timeLimit, total_points: totalPoints, variant_label: variantLabel };
  }

  function submitForm(variant: string, shuffle: boolean, asWord: boolean) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/questions/exam-paper.php';
    form.target = '_blank';
    form.style.display = 'none';

    const append = (name: string, value: string) => {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = name;
      i.value = value;
      form.appendChild(i);
    };

    append('question_ids', JSON.stringify(cart));
    append('title', title);
    append('instructions', instructions);
    append('school_name', schoolName);
    append('subject_name', subjectName);
    append('grade_level', gradeLevel);
    append('time_limit', timeLimit);
    append('total_points', totalPoints);
    append('teacher_name', teacherName);
    append('exam_date', examDate);
    append('variant_label', variant);
    append('include_answer_key', includeAnswerKey ? '1' : '0');
    append('shuffle_options', shuffle ? '1' : '0');
    append('columns', String(columns));
    if (asWord) append('as', 'word');
    const token = getToken();
    if (token) append('token', token);

    document.body.appendChild(form);
    form.submit();
    setTimeout(() => form.remove(), 1000);
  }

  function handleCreatePdf() {
    if (!title.trim()) { setTitleError(t('exam.field.title')); return; }
    setTitleError('');
    submitForm(variantLabel, shuffleOptions, false);
    onClose();
  }

  function handleCreateWord() {
    if (!title.trim()) { setTitleError(t('exam.field.title')); return; }
    setTitleError('');
    submitForm(variantLabel, shuffleOptions, true);
    onClose();
  }

  function handleMultiGenerate() {
    if (!title.trim()) { setTitleError(t('exam.field.title')); return; }
    setTitleError('');
    const labels = ['A', 'B', 'C', 'D', 'E'].slice(0, versionCount);
    labels.forEach((label) => {
      setTimeout(() => submitForm(label, true, false), 100);
    });
    onClose();
  }

  const meta = buildMetadata();

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('exam.builder_title')}
        description={t('exam.builder_desc')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSaveSetOpen(true)}>
              {t('exam.btn.save_set')}
            </Button>
            <Button variant="secondary" onClick={() => setMultiMode((v) => !v)}>
              {t('exam.btn.multi_version')}
            </Button>
            <Button variant="secondary" leftIcon={<FileText className="w-4 h-4" />} onClick={handleCreatePdf}>
              {t('exam.btn_pdf')}
            </Button>
            <Button leftIcon={<FileText className="w-4 h-4" />} onClick={handleCreateWord}>
              {t('exam.btn_word')}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Input
                label={t('exam.field.title')}
                placeholder={t('exam.field.title_ph')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={titleError}
              />
            </div>
            <Input
              label={t('exam.field.school_name')}
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
            />
            <Input
              label={t('exam.field.subject_name')}
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
            />
            <Input
              label={t('exam.field.grade_level')}
              value={gradeLevel}
              onChange={(e) => setGradeLevel(e.target.value)}
            />
            <Input
              label={t('exam.field.teacher_name')}
              value={teacherName}
              onChange={(e) => setTeacherName(e.target.value)}
            />
            <Input
              type="date"
              label={t('exam.field.exam_date')}
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
            />
            <Input
              label={t('exam.field.time_limit')}
              placeholder={t('exam.field.time_limit_ph')}
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
            />
            <Input
              type="number"
              label={`${t('exam.field.total_points')} (${t('exam.auto_points')})`}
              value={totalPoints}
              min={0}
              onChange={(e) => setTotalPoints(e.target.value)}
            />
            <Input
              label={t('exam.field.variant_label')}
              placeholder={t('exam.field.variant_ph')}
              value={variantLabel}
              onChange={(e) => setVariantLabel(e.target.value)}
            />
          </div>

          <Textarea
            label={t('exam.field.instructions')}
            placeholder={t('exam.field.instructions_ph')}
            value={instructions}
            rows={3}
            onChange={(e) => setInstructions(e.target.value)}
          />

          <div className="flex flex-wrap gap-6 pt-1">
            <Switch
              checked={includeAnswerKey}
              onChange={(e) => setIncludeAnswerKey(e.target.checked)}
              label={t('exam.switch.answer_key')}
            />
            <Switch
              checked={shuffleOptions}
              onChange={(e) => setShuffleOptions(e.target.checked)}
              label={t('exam.switch.shuffle')}
            />
          </div>

          <div className="flex items-center gap-4 pt-1">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('exam.col_label')}</span>
            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
              <input type="radio" name="columns" value="1" checked={columns === 1} onChange={() => setColumns(1)} className="accent-green-500" />
              {t('exam.col_1')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
              <input type="radio" name="columns" value="2" checked={columns === 2} onChange={() => setColumns(2)} className="accent-green-500" />
              {t('exam.col_2')}
            </label>
          </div>

          {multiMode && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-medium text-amber-800">{t('exam.multi_title')}</p>
              <p className="text-xs text-amber-700">{t('exam.multi_desc')}</p>
              <div className="flex items-end gap-3">
                <Input
                  type="number"
                  label={t('exam.multi_count_label')}
                  value={String(versionCount)}
                  min={2}
                  max={5}
                  className="w-24"
                  onChange={(e) => setVersionCount(Math.min(5, Math.max(2, Number(e.target.value))))}
                />
                <Button onClick={handleMultiGenerate}>{t('exam.btn.generate_multi')}</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <SaveExamSetModal
        open={saveSetOpen}
        onClose={() => setSaveSetOpen(false)}
        cart={cart}
        metadata={meta}
      />
    </>
  );
}
